import Button from "@/components/Button/index.js";
import EFormRenderer from "@/components/EFormV2/EFormRenderer";
import FloatingErrorButton from "@/components/FloatingErrorButton/index";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import useEForm from "@/hooks/use-eform/index";
import { useHealthdex } from "@/hooks/use-healthdex/index";
import { useLocation, useNavigate } from "@/lib/router-dom/index";
import { FormatEFORMErrorMessage, RemoveDuplicatesByKey } from "@/pages/eform/utils";
import addFormats from "@/utils/custom-ajv-formats";
import { getEnvAndDex } from "@/utils/get-env-and-dex";
import Ajv from "ajv";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { ajvErrorMapping } from "@/utils/ajv-error-mapping/index";
import { featureConstant } from '@/constants/configuration/feature.js'
import FloatingPanel from '@/componentsDS/FloatingPanel/FloatingPanel.jsx'
import { FloatingPanelType } from '@/componentsDS/FloatingPanel/FloatingPanel.type.js'


const ajv = new Ajv({ allErrors: true, verbose: true });
addFormats(ajv);

const EFormPage = () => {
	const shareDataPageUri = "/shared-data/share";

	const { features } = useSelector(state => state.auth)
	const isErrorEnhancementEnabled = features?.includes(featureConstant.ERROR_MESSAGE_ENHANCEMENT);
	const isMasterDataValidationEnabled = features?.includes(featureConstant.MASTER_DATA_VALIDATION);
	const isConsumerBasedMasterDataValidationEnabled = features?.includes(featureConstant.CONSUMER_BASED_MASTER_DATA_VALIDATION);
	
	const { dex } = getEnvAndDex();
	const isHealthDex = useHealthdex();

	const { goTo } = useNavigate();
	const { currentState } = useLocation();

	const {
		payload,
		setPayload,
		isDirty,
		setIsDirty,
		getFullJson,
		extractCombinedSchema,
		getSchemaWithoutAttachment
	} = useEForm();

	const [schema, setSchema] = useState(null);
	const [isValidForm, setIsValidForm] = useState(false);
	const [isValidFormat, setIsValidFormat] = useState(true);
	const [errorListToDisplay, setErrorListToDisplay] = useState([]);
	const [isSubmitClicked, setIsSubmitClicked] = useState(false);

	const masterData = useSelector(state => state.shareDataElement?.masterData);
	const consumerMasterData = useSelector(state => state.shareDataElement?.consumerMasterData);
	
	const floatingPanelRef = useRef(null);

	const state = currentState?.shareDataElementState;

	const floatingPanelErrorMessage = useMemo(() => {
		return Object.values(errorListToDisplay).flat().map(message => {
			// Split the message at the first colon to separate field name from error text
			const parts = message.split(/:(.*)/s);
			
			if (parts.length >= 2) {
			  return {
				message: (
				  <>
					<strong>{parts[0]}</strong>:{parts[1]}
				  </>
				)
			  };
			}
			
			// If no colon found, return the original message
			return { message };
		  });
	}, [errorListToDisplay]);
	const breadcrumbItem = [
		{
			label: '',
			link: `/`,
			isHomePage: true
		},
		{
			label: 'Shared Data',
			link: `/shared-data`
		},
		{
			label: 'Share Data',
			link: `/shared-data/share`
		}

	]

	useEffect(() => {
		if (!currentState) {
			goTo(shareDataPageUri);
			return;
		}

		const elementSchema = currentState.dataElementSchema
		let { payload } = state

		if (Array.isArray(payload)) {
			payload = payload[0];
		}

		const plainSchema = extractCombinedSchema(
			elementSchema,
			payload
		);
		const schemaWithoutAttachment = getSchemaWithoutAttachment(plainSchema);

		const effectiveMasterData = isConsumerBasedMasterDataValidationEnabled
			? (consumerMasterData?.masterData ?? masterData)
			: masterData;
		const effectiveMappings = isConsumerBasedMasterDataValidationEnabled
			? (consumerMasterData?.masterDataMappings ?? elementSchema.masterDataMappings)
			: elementSchema.masterDataMappings;

		let enhancedSchema = schemaWithoutAttachment;
		if (isMasterDataValidationEnabled && effectiveMappings && effectiveMasterData) {
			enhancedSchema = {
				...schemaWithoutAttachment,
				masterData: effectiveMasterData,
				masterDataValidation: effectiveMappings
			};
		}

		setSchema(enhancedSchema);
		
		setPayload(getFullJson(enhancedSchema, payload, plainSchema));
	}, [currentState, masterData, consumerMasterData]);

	useEffect(() => {
		if (payload) {
			validateSchema();
			(isHealthDex || isErrorEnhancementEnabled) && validateErrors();
		}
	}, [payload]);

	function validateSchema() {
		const validate = ajv.compile(schema);
		const { attachments, ...payloadToBeValidate } = payload;
		const isPayloadValid = validate(payloadToBeValidate);
		const isValid = isValidFormat && isPayloadValid;
		setIsValidForm(isValid);
		return isPayloadValid;
	}

	function validateErrors() {
		const validate = ajv.compile(schema);
		if (isErrorEnhancementEnabled) {
			const errorMessages = ajvErrorMapping(validate.errors, schema, payload);
			setErrorListToDisplay(errorMessages);
		} else {
			const errorMessages = validate.errors?.map(a => FormatEFORMErrorMessage(a, state?.dataElement?.id, schema));
			setErrorListToDisplay(RemoveDuplicatesByKey(errorMessages?.filter(e => e), "message"));
		}		
	}

	const props = {
		schema,
		payload,
		setPayload,
		isDirty,
		setIsValidFormat,
		errorListToDisplay
	};

	function onCancelBtnClick() {
		goTo(shareDataPageUri, state);
	}

	function onSubmitBtnClick() {
		setIsSubmitClicked(true);
		const validateResult = validateSchema();
		setIsDirty();

		if (validateResult) {
			state.payload = [payload]
			if (state.action?.type === "") {
				state.action = { type: "EFORM", value: "Filled via E-form" }
			}

			goTo(shareDataPageUri, state);
		} else {
			if (isErrorEnhancementEnabled) {
				validateErrors();
			}
			setIsDirty(true);
			floatingPanelRef.current?.open();
		}
	}

	if (!currentState || !payload) {
		return <></>;
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbItem} />
			<div className="font-size-xxl font-weight-bold">Data E-Form</div>
			<div className="eform-form-container mt-1">
				<div className="flex-box flex-space-between mb-1">
					<div data-testid="eform-page-form-title" className="eform-page-form-title font-color-grey-100 font-size-xl font-weight-bold">
						{schema.title ?? schema.description}
					</div>
					<br />
					{!isHealthDex &&
						<div className="eform-page-form__required-fields font-color-grey-60 font-size-m font-weight-bold">
							Some fields are auto-filled. Fields marked with an asterisk (*) are mandatory.
						</div>
					}
				</div>
				<div className="eform-header-border"></div>
				<div className="eform-form">
						<EFormRenderer {...props} isSubmitClicked={isSubmitClicked} />
				</div>

				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button
							data-testid="eform-page-cancel-btn"
							text="Cancel"
							sizing="Medium"
							full
							noborder
							maxWidth="154"
							submit={() => onCancelBtnClick()}
						/>
					</div>

					<div className="layout-default__sticky-actions__right">
						<Button
							data-testid="eform-page-submit-btn"
							text="Submit"
							sizing="Medium"
							full
							maxWidth="154"
							disabled={!isHealthDex && !isValidForm && !isErrorEnhancementEnabled}
							submit={() => onSubmitBtnClick()}
						/>
					</div>
				</div>

				{isHealthDex && !isErrorEnhancementEnabled && isDirty && !isValidForm && <FloatingErrorButton errorList={errorListToDisplay} addClass="feb_mb-21" />}
				{isErrorEnhancementEnabled && floatingPanelErrorMessage.length > 0 && isDirty && (
					<FloatingPanel
						title={`${floatingPanelErrorMessage.length} errors found`}
						messages={floatingPanelErrorMessage}
						type={FloatingPanelType.DESTRUCTIVE}
						className='eform-floating-panel'
						ref={floatingPanelRef}
					/>
				)}
			</div>
		</>
	);
};

export default EFormPage;
